<template>
  <Modal name="ADDITIONAL_INFO_APPOINTMENT_MODAL" :title="$t('appointment.additionalData.modalTitle')" width="700">
    <template v-slot:body>
      <!-- form -->
      <input v-model="id" type="hidden" />
      <div class="d-flex">
        <v-select v-model="type" :items="types" :disabled="loading" :label="$t('appointment.additionalData.type')" class="mw-100 mr-2" hide-details="auto" outlined />
        <v-text-field v-model="title" :disabled="loading" :label="$t('appointment.additionalData.title')" hide-details="auto" class="mw-150 mr-2"  outlined />

        <!-- type field -->
        <div v-if="type === null" class="field-empty-state" />
        <HPhone v-show="type === 'PHONE'" v-model="value" :default-value="defaultValue" :country="country" ref="phone" />
        <v-text-field v-show="type === 'LINK' || type === 'OTHER' || type === 'EMAIL'" v-model="value" :label="$t(`appointment.additionalData.types.${type}`)" outlined />
        <!-- /type field -->
        <div class="text-right ml-2" v-if="type">
          <v-btn @click="addDetail" color="primary" class="mt-3" icon>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="pointer" color="primary" v-bind="attrs" v-on="on">
                  mdi-plus-circle
                </v-icon>
              </template>
              <span>{{ $t('appointment.additionalData.add') }}</span>
            </v-tooltip>
          </v-btn>
        </div>
      </div>
      <!-- form -->

      <!-- list -->
      <span v-if="additionalDataListRequest.length >= 1">
        <v-divider :class="`${type ? 'mt-0' : 'mt-6'} mb-5`" />

        <h5 class="grey--text pt-0">
          {{ $t('appointment.additionalData.fields') }}
        </h5>

        <div class="px-4">
          <v-row>
            <v-col cols="4">
              <h6 class="text-uppercase grey--text pt-0">
                {{ $t('appointment.additionalData.title') }}
              </h6>
            </v-col>
            <v-col>
              <h6 class="text-uppercase grey--text pt-0">
                {{ $t('appointment.additionalData.value') }}
              </h6>
            </v-col>
          </v-row>
        </div>

        <v-card outlined>
          <span v-for="(item, index) in additionalDataListRequest" :key="index">
            <v-row class="pl-4 py-1" no-gutters>
              <v-col cols="4">
                <span class="d-block body-2 mt-2">{{ item.title }}</span>
              </v-col>
              <v-col cols="5">
                <span class="d-block body-2 mt-2">{{ item.value }}</span>
              </v-col>
              <v-col cols="3" align="center">
                <div class="d-flex justify-end">
                  <v-btn @click="editDetail(item, index)" class="mr-2" icon>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon class="pointer" color="primary" v-bind="attrs" v-on="on" small>
                          mdi-pencil
                        </v-icon>
                      </template>
                      <span>
                        {{ $t('appointment.additionalData.edit') }}
                      </span>
                    </v-tooltip>
                  </v-btn>
                  <v-btn @click="removeDetail(item, index)" class="mr-2" icon>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon class="pointer" color="error" v-bind="attrs" v-on="on" small>
                          mdi-delete
                        </v-icon>
                      </template>
                      <span>
                        {{ $t('appointment.additionalData.remove') }}
                      </span>
                    </v-tooltip>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
            <v-divider />
          </span>
        </v-card>
      </span>
      <!-- /list -->

      <!-- no additional data -->
      <v-alert v-else class="grey lighten-4 mt-6">
        <span v-if="isLoading" class="d-block text-center">
          <v-progress-linear indeterminate rounded />
        </span>
        <span v-else class="d-block text-center">
          {{ $t('appointment.additionalData.noAdditionalData') }}
        </span>
      </v-alert>
      <!-- /no additional data -->

      <div class="d-flex justify-end mt-8">
        <v-btn @click="checkBeforeClose" :disabled="loading" class="mr-5" elevation="0" large text>
          <span class="body-2">
            {{ $t('appointment.additionalData.modal.cancel') }}
          </span>
        </v-btn>
        <v-btn @click="submitAdditionalData" :loading="loading" color="primary" elevation="0" large>
          <span class="body-2">
            {{ $t('appointment.additionalData.modal.submit') }}
          </span>
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { api } from '../../../services'
import { email, required } from '@/util/errorHandling'

import Modal from '@/components/app/Modal'
import HPhone from '../../app/HPhone.vue'

export default {
  name: 'ModalUpdateAdditionalInfo',

  components: {
    Modal,
    HPhone,
  },

  watch: {
    appointment(value) {
      this.resetFields()
      this.hasChanged = false
      this.additionalDataListRequest = []
      this.deleteAdditionalDataRequest = []

      if (value.additional_data) {
        this.additionalDataListRequest = value.additional_data
      }
    },

    type(value) {
      this.value = null

      if (value === 'PHONE') {
        this.$refs.phone.currentCountryCode = '+55'
      }

      if (value === 'LINK') {
        this.value = 'https://'
      }
    },
  },

  computed: {
    ...mapGetters(['appointment', 'isLoading']),

    types() {
      return [
        {
          text: this.$t('appointment.additionalData.types.PHONE'),
          value: 'PHONE',
        },
        {
          text: this.$t('appointment.additionalData.types.LINK'),
          value: 'LINK',
        },
        {
          text: this.$t('appointment.additionalData.types.OTHER'),
          value: 'OTHER',
        },
        {
          text: this.$t('appointment.additionalData.types.EMAIL'),
          value: 'EMAIL',
        }
      ]
    },

    isFieldsEmpty() {
      return !this.type || !this.title || !this.value
    },

    country: {
      get() {
        return localStorage.getItem('user_country')
      },
      set(value) {
        return value
      }
    },
  },

  methods: {
    ...mapActions(['openModal']),
    ...mapMutations(['closeAllModals']),

    checkBeforeClose() {
      if (this.deleteAdditionalDataRequest.length > 0 || this.hasChanged) {
        this.openModal('ADDITIONAL_INFO_APPOINTMENT_CONFIRM_TO_CLOSE_MODAL')
        return
      }

      this.closeAllModals()
    },

    resetFields() {
      this.id = null
      this.type = null
      this.title = null
      this.value = null
      this.defaultValue = null
      this.itemIndex = null
    },

    removeDetail(item, index) {
      if (item.id) this.deleteAdditionalDataRequest.push(item.id)

      this.additionalDataListRequest.splice(index, 1)
      this.resetFields()
    },

    editDetail(item, index) {
      this.id = item.id
      this.type = item.type
      this.title = item.title

      this.$nextTick(() => {
        if (this.id) {
          this.itemIndex = this.additionalDataListRequest.findIndex((item) => item.id === this.id)
        } else {
          this.itemIndex = index
        }
      })

      this.$nextTick(() => {
        if (item.type === 'PHONE') this.defaultValue = item.value
        else this.value = item.value
      })
    },

    addDetail() {
      if (this.isFieldsEmpty) {
        this.$store.dispatch('showErrorMessage', 'Please fill all fields')
        return
      }

      if (this.itemIndex !== null) {
        this.additionalDataListRequest[this.itemIndex] = {
          id: this.id ?? null,
          title: this.title,
          value: this.value,
          type: this.type,
        }
      } else {
        this.additionalDataListRequest.push({
          title: this.title,
          value: this.value,
          type: this.type,
        })
      }

      this.resetFields()
      this.hasChanged = true
    },

    async submitAdditionalData() {
      this.loading = true

      await api.put(`/admin/appointment/${this.appointment.id}/updateAdditionalData`, {
          additional_data_list_request: this.additionalDataListRequest,
          delete_additional_data_request: this.deleteAdditionalDataRequest
        })
        .then(() => {
          this.resetFields()
          this.closeAllModals()
          this.$emit('refreshMap')
          this.$store.dispatch('showSuccessMessage', 'Additional info added successfully')
          this.additionalDataListRequestControl = []
        })
        .catch(({ data }) => {
          this.$store.dispatch('showErrorMessage', data?.message || 'Error adding additional info')
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  data() {
    return {
      loading: false,
      hasChanged: false,

      additionalDataListRequest: [],
      deleteAdditionalDataRequest: [],

      additionalDataListRequestControl: [],

      id: null,
      type: null,
      title: null,
      value: null,
      defaultValue: null,
      itemIndex: null,

      rules: {
        email,
        required,
      }
    }
  }
}
</script>

<style>
.field-empty-state {
  width: 100%;
  height: 55px;
  border-radius: 4px;
  background-color: #f5f5f5;
}
</style>
